import {
  Box,
  ButtonGroup,
  Container,
  Divider,
  FormLabel,
  IconButton,
  SkeletonText,
  Table,
  Tabs,
  Tag,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { ColumnDef, flexRender } from "@tanstack/react-table";
import iconArrowLeftRight from "assets/icons/icon-arrow-left-right.svg";
import IconEye from "assets/icons/icon-eye.svg?react";
import CustomRangeDatepicker from "components/ui/Datepicker/CustomRangeDatepicker";
import DateTime from "components/ui/DateTime";
import EmailRowTitle from "components/ui/EmailRowTitle";
import EmptyList from "components/ui/EmptyList";
import FilterGroup from "components/ui/FilterGroup";
import Pagination from "components/ui/Pagination";
import ReviewFilter, { ReviewFilterValue } from "components/ui/ReviewFilter";
import SearchInput from "components/ui/SearchInput";
import TagEmailStatus from "components/ui/TagEmailStatus";
import ServerDynamicTableProvider, {
  useServerDynamicTableContext,
} from "contexts/server-table/ServerDynamicTableContext";
import useServerTableUrlState from "hooks/server-table/useServerTableUrlState";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../components/Header";

type NewsletterFilterKey = "reviewed" | "sentAtFrom" | "sentAtTo";

const newsletterFilterDefaults: Record<NewsletterFilterKey, string> = {
  reviewed: "all",
  sentAtFrom: "",
  sentAtTo: "",
};

export default function PageNewsletters() {
  const location = useLocation();
  const {
    pagination,
    globalFilter,
    filters,
    setPagination,
    setGlobalFilter,
    setFilter,
  } = useServerTableUrlState<NewsletterFilterKey>({
    filterDefaults: newsletterFilterDefaults,
  });

  const reviewedFilter = (filters.reviewed ?? "all") as ReviewFilterValue;
  const selectedDateRange = React.useMemo(() => {
    const dates: Date[] = [];
    const fromDate = parseIsoDate(filters.sentAtFrom);
    const toDate = parseIsoDate(filters.sentAtTo);

    if (fromDate) {
      dates.push(fromDate);
    }
    if (toDate) {
      dates.push(toDate);
    }

    return dates;
  }, [filters.sentAtFrom, filters.sentAtTo]);

  const resourceParams = React.useMemo(() => {
    return {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      q: globalFilter || undefined,
      reviewed: reviewedFilter === "all" ? undefined : reviewedFilter,
      sentAtFrom: filters.sentAtFrom || undefined,
      sentAtTo: filters.sentAtTo || undefined,
    };
  }, [
    filters.sentAtFrom,
    filters.sentAtTo,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    reviewedFilter,
  ]);

  const columns = React.useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        header: "Assunto",
        accessorKey: "subject",
        cell: ({ row }) => (
          <EmailRowTitle title={row.original.subject as string}>
            {(row.original.reviewedByUserId as boolean) && (
              <Tag ml="15px" size="sm" colorScheme="gray">
                Revisado
              </Tag>
            )}
          </EmailRowTitle>
        ),
      },
      {
        header: "Lista",
        accessorKey: "list.name",
      },
      {
        header: "Status",
        cell: ({ row }) => <TagEmailStatus record={row.original} />,
      },
      {
        header: "Destinatários",
        accessorKey: "email.countRecipients",
      },
      {
        header: "Enviados",
        accessorKey: "email.countSent",
      },
      {
        header: "Bounce",
        accessorKey: "email.countBounce",
      },
      {
        id: "sentAt",
        header: "Enviado em",
        accessorKey: "email.createdAt",
        cell: (props) => <DateTime date={props.cell.getValue() as string} />,
      },
    ],
    []
  );


  return (
    <ServerDynamicTableProvider
      columns={columns}
      pagination={pagination}
      globalFilter={globalFilter}
      onPaginationChange={setPagination}
      onGlobalFilterChange={setGlobalFilter}
      resource={{
        resourceName: "newsletters",
        params: resourceParams,
      }}
    >
      <Tabs index={0} variant="line">
        <Header />

        <Divider />

        <Container flexDirection="column">
          <Filters
            globalFilter={globalFilter}
            onGlobalFilterChange={setGlobalFilter}
            reviewFilter={reviewedFilter}
            onReviewFilterChange={(value) => setFilter("reviewed", value)}
            dateRange={selectedDateRange}
            onDateRangeChange={(dates) => {
              const [startDate, endDate] = dates;
              setFilter("sentAtFrom", startDate ? startOfDayIso(startDate) : "");
              setFilter("sentAtTo", endDate ? endOfDayIso(endDate) : "");
            }}
          />
          <List detailSearch={location.search} />
        </Container>
      </Tabs>
    </ServerDynamicTableProvider>
  );
}

interface FiltersProps {
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  reviewFilter: ReviewFilterValue;
  onReviewFilterChange: (value: ReviewFilterValue) => void;
  dateRange: Date[];
  onDateRangeChange: (dates: Date[]) => void;
}

function Filters({
  globalFilter,
  onGlobalFilterChange,
  reviewFilter,
  onReviewFilterChange,
  dateRange,
  onDateRangeChange,
}: FiltersProps) {

  return (
    <FilterGroup justifyContent="space-between" zIndex={1} pos="relative">
      <Box flex={1} minW="180px">
        <FormLabel>Buscar</FormLabel>
        <SearchInput
          placeholder="Buscar por assunto ou nome da lista"
          value={globalFilter}
          onChange={(value) => onGlobalFilterChange(String(value))}
        />
      </Box>
      <Box>
        <ReviewFilter
          value={reviewFilter}
          onChange={onReviewFilterChange}
        />
      </Box>
      <Box>
        <FormLabel>Filtrar por período</FormLabel>
        <CustomRangeDatepicker
          dates={dateRange}
          onDateChange={onDateRangeChange}
        />
      </Box>
    </FilterGroup>
  );
}

function List({ detailSearch }: { detailSearch: string }) {
  const { table, queryResult } = useServerDynamicTableContext();

  const total = queryResult?.data?.meta?.total || 0;
  const isLoading = queryResult?.isLoading || false;

  const hasData = total > 0;
  const hasResult = table.getRowModel().rows.length > 0;

  return (
    <SkeletonText isLoaded={!isLoading}>
      {hasResult && (
        <>
          <Table mt="25px">
            <Thead>
              <Tr>
                <Th>Assunto</Th>
                <Th>Lista</Th>
                <Th w="100px">Status</Th>
                <Th w="100px">Destinatários</Th>
                <Th w="100px">Enviados</Th>
                <Th w="100px">Bounce</Th>
                <Th w="100px">Enviado em</Th>
                <Th w="100px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const rowData = row.original as Record<string, unknown>;

                return (
                  <Tr key={`row-${row.id}`}>
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                    <Td>
                      <ButtonGroup>
                        <IconButton
                          aria-label="Visualizar"
                          size="sm"
                          icon={<IconEye />}
                          as={Link}
                          to={{
                            pathname: `/disparos-de-newsletter/${String(rowData.id)}`,
                            search: detailSearch,
                          }}
                        />
                      </ButtonGroup>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>

          <Pagination table={table} />
        </>
      )}

      {(!hasData || !hasResult) && (
        <EmptyList
          mt="25px"
          icon={iconArrowLeftRight}
          title={
            !hasData
              ? "Você ainda não possui nenhum disparo de Newsletter cadastrado"
              : "Nenhum resultado encontrado"
          }
          description=""
          isTable
        />
      )}
    </SkeletonText>
  );
}

const parseIsoDate = (date?: string) => {
  if (!date) {
    return null;
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const startOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate.toISOString();
};

const endOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate.toISOString();
};
